import React from "react";
import { Box, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import StadiumIcon from "@mui/icons-material/Stadium";
import type { TeamPlayerWithCLV } from "../types/draft";
import { DOME_STADIUMS } from "../services/stackDetection";
import { teamColorBg } from "../helpers/colorHelpers";
import { useDraftData } from "../context/DraftDataContext";
import TeamDataUnavailable from "./ui/TeamDataUnavailable";

interface MatchupEntry {
  matchup: string;
  stackTotal: number;
  homeTeam: string;
  awayTeam: string;
  homeTeamPlayers: TeamPlayerWithCLV[];
  awayTeamPlayers: TeamPlayerWithCLV[];
  isDome: boolean;
}

interface StackGroup {
  stackTotal: number;
  stacks: MatchupEntry[];
}

function getWeekGameStacks(
  teamPlayers: TeamPlayerWithCLV[],
  weekIdx: number,
  weekSchedules: Record<string, string[]>
): StackGroup[] {
  const teamPlayersByNFL: Record<string, TeamPlayerWithCLV[]> = {};
  teamPlayers.forEach(p => {
    if (!p.team) return;
    if (!teamPlayersByNFL[p.team]) teamPlayersByNFL[p.team] = [];
    teamPlayersByNFL[p.team].push(p);
  });

  const matchupMap: Record<string, { homeTeam: string; awayTeam: string; homeTeamPlayers: TeamPlayerWithCLV[]; awayTeamPlayers: TeamPlayerWithCLV[] }> = {};
  Object.keys(teamPlayersByNFL).forEach(nflTeam => {
    const scheduleArr = weekSchedules[nflTeam];
    if (!scheduleArr || scheduleArr.length < weekIdx + 1) return;
    const matchupStr = scheduleArr[weekIdx];
    const isAway = matchupStr.startsWith("@");
    const oppTeam = (isAway
      ? matchupStr.slice(1)
      : matchupStr.toLowerCase().startsWith("vs")
      ? matchupStr.slice(2)
      : matchupStr  // home game: full string IS the opponent
    ).trim();
    if (!oppTeam || oppTeam.toUpperCase() === "BYE") return;
    if (!teamPlayersByNFL[oppTeam]) return;

    const homeTeam = isAway ? oppTeam : nflTeam;
    const awayTeam = isAway ? nflTeam : oppTeam;
    const matchupKey = `${awayTeam}@${homeTeam}`;

    if (!matchupMap[matchupKey]) {
      matchupMap[matchupKey] = { homeTeam, awayTeam, homeTeamPlayers: [], awayTeamPlayers: [] };
    }
    if (isAway) {
      matchupMap[matchupKey].awayTeamPlayers = teamPlayersByNFL[nflTeam];
    } else {
      matchupMap[matchupKey].homeTeamPlayers = teamPlayersByNFL[nflTeam];
    }
  });

  const stacksArr: MatchupEntry[] = Object.values(matchupMap)
    .filter(m => m.homeTeamPlayers.length > 0 && m.awayTeamPlayers.length > 0)
    .map(m => ({
      matchup: `${m.awayTeam} @ ${m.homeTeam}`,
      stackTotal: m.homeTeamPlayers.length + m.awayTeamPlayers.length,
      homeTeam: m.homeTeam,
      awayTeam: m.awayTeam,
      homeTeamPlayers: m.homeTeamPlayers,
      awayTeamPlayers: m.awayTeamPlayers,
      isDome: DOME_STADIUMS.has(m.homeTeam),
    }))
    .sort((a, b) => b.stackTotal - a.stackTotal);

  const groupedByTotal: Record<string, MatchupEntry[]> = {};
  stacksArr.forEach(s => {
    const key = String(s.stackTotal);
    if (!groupedByTotal[key]) groupedByTotal[key] = [];
    groupedByTotal[key].push(s);
  });
  return Object.entries(groupedByTotal)
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([stackTotal, stacks]) => ({ stackTotal: Number(stackTotal), stacks }));
}

interface WeekStacksSidebarProps {
  teamPlayers: TeamPlayerWithCLV[];
  TEAM_COLORS: Record<string, string>;
  weekNums: number[] | null | undefined;
  weekSchedules: Record<string, string[]> | null | undefined;
}

export default function WeekStacksSidebar({ teamPlayers, TEAM_COLORS, weekNums, weekSchedules }: WeekStacksSidebarProps) {
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";
  const { hasTeamData } = useDraftData();

  if (!hasTeamData) {
    return <TeamDataUnavailable feature="Playoff stacks" dense />;
  }

  if (!weekNums || !weekSchedules) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ fontSize: 13, color: "text.disabled", fontStyle: "italic" }}>
          2026 schedule not available yet.
        </Typography>
      </Box>
    );
  }

  // Filter to playoff weeks only (weeks 15, 16, 17)
  const playoffWeekIndices = weekNums
    .map((weekNum, idx) => weekNum >= 15 && weekNum <= 17 ? { weekNum, idx } : null)
    .filter(Boolean) as Array<{ weekNum: number; idx: number }>;

  return (
    <Box sx={{ width: "100%" }}>
      {playoffWeekIndices.map(({ weekNum, idx }) => {
        const weekStacks = getWeekGameStacks(teamPlayers, idx, weekSchedules);
        return (
          <Box key={weekNum} sx={{ mb: 2, mt: idx > 0 ? 4 : 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1 }}>
              Week {weekNum} Bring Backs
            </Typography>
            {weekStacks.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                No bring-backs.
              </Typography>
            ) : (
              weekStacks.map(group => (
                <Box key={group.stackTotal} sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.75 }}>
                    {group.stackTotal}-player matchups
                  </Typography>
                  {group.stacks.map((s, i) => (
                    <Box key={s.matchup + i} sx={{ mb: 0.5 }}>
                      <Stack direction="row" gap={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                        <Chip
                          label={`${s.awayTeam} (${s.awayTeamPlayers.length})`}
                          size="small"
                          sx={{ bgcolor: TEAM_COLORS[s.awayTeam] ? teamColorBg(TEAM_COLORS[s.awayTeam], isDark) : "action.selected", color: TEAM_COLORS[s.awayTeam] ? "#fff" : "text.primary", fontWeight: 500, minWidth: 36 }}
                        />
                        <Typography variant="body2" sx={{ mx: 0.5 }}>@</Typography>
                        <Chip
                          label={`${s.homeTeam} (${s.homeTeamPlayers.length})`}
                          size="small"
                          sx={{ bgcolor: TEAM_COLORS[s.homeTeam] ? teamColorBg(TEAM_COLORS[s.homeTeam], isDark) : "action.selected", color: TEAM_COLORS[s.homeTeam] ? "#fff" : "text.primary", fontWeight: 500, minWidth: 36 }}
                        />
                        {s.isDome && (
                          <Tooltip title="Dome game" arrow>
                            <StadiumIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        );
      })}
    </Box>
  );
}
